import { customElement, property } from "lit/decorators.js";
import { FilterElement, Label } from "@/components/search/filter_element";
import { html, TemplateResult } from "lit";
import { watchMixin } from "@/components/meta/watch_mixin";
import { ClearInitialContentController } from "@/components/meta/clear_initial_content_controller";
import { generateUniqueId } from "@/utilities";

/**
 * This component inherits from FilterCollectionElement.
 * It represents a dropdown which allows to select one label, with the currently selected label shown as a button
 *
 * @element d-standalone-dropdown-filter
 *
 * @prop {string} param - The searchQuery param to be used for this filter
 * @prop {string} default - The default value for the filter
 * @prop {[Label]} labels - All labels that could potentially be selected
 * @prop {boolean} multi - Whether multiple items can be selected
 */
@customElement("d-standalone-dropdown-filter")
export class StandaloneDropdownFilter extends watchMixin(FilterElement) {
    clearInitialContent = new ClearInitialContentController(this);

    @property()
    multi = false;

    @property({ type: String })
    default: string = "";

    private readonly uniqueId = generateUniqueId();
    private get dropdownId(): string {
        const safeParam = (this.param || "filter").replace(/[^a-zA-Z0-9_-]/g, "_");
        return `dropdownMenuLink-${safeParam}-${this.uniqueId}`;
    }

    onItemClick(e: Event): void {
        e.preventDefault();
        const el = e.currentTarget as HTMLElement;
        const id = el.dataset.labelId;
        const label = this.labels.find((l) => l.id === id);

        this.select(label);
    }

    watch = {
        default: () => {
            if (this.getSelectedLabels().length === 0) {
                const found = this.labels.find((label) => label.id === this.default);
                if (found) {
                    this.select(found);
                }
            }
        },
    };

    render(): TemplateResult {
        if (this.labels.length <= 1) {
            return html``;
        }

        const selected =
            this.getSelectedLabels()[0] ?? this.labels.find((l) => l.id === this.default) ?? this.labels[0];

        return html`
            <div class="dropdown">
                <button
                    type="button"
                    class="btn btn-text dropdown-toggle"
                    id="${this.dropdownId}"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-haspopup="true"
                >
                    ${selected?.name ?? ""}
                    <i class="mdi mdi-chevron-down mdi-18 right-icon" aria-hidden="true"></i>
                </button>

                <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="${this.dropdownId}">
                    ${this.labels.map(
                        (s) => html`
                            <li>
                                <button
                                    type="button"
                                    class="dropdown-item ${this.isSelected(s) ? "active" : ""}"
                                    data-label-id="${s.id}"
                                    @click=${this.onItemClick}
                                    aria-current="${this.isSelected(s) ? "true" : "false"}"
                                >
                                    ${s.name}
                                </button>
                            </li>
                        `,
                    )}
                </ul>
            </div>
        `;
    }
}
